import { Legend, Root, Series, p50, percent } from "@amcharts/amcharts5";
import { XYChart } from "@amcharts/amcharts5/xy";
import { isEmpty, orderBy } from "lodash";

import { ChartName, ChartSettings, LegendData } from "@/classes/BaseChart/index";
import Lang from "@/lang";
import { getChartAllSettings } from "@/settings/charts";
import { chartFontSettings } from "@/settings/widgets";
import { AmCharts } from "@/types/charts";
import { InputDictionary } from "@/types/common";

const MAX_WIDTH_LEGEND = 200;
const CORNER_RADIUS_LEGEND = 10;
type LegendNames = { name: string; index: number };

class ChartWithLegend {
  declare settings: ChartSettings;
  declare chart: AmCharts;
  declare root: Root;
  declare chartName: ChartName;
  declare module: string;
  declare inputsDictionary: Record<string, InputDictionary[]>;
  declare metricsDictionary: InputDictionary[];

  private getMetricsDictionary(): void {
    const allSettings = getChartAllSettings(this.chartName, this.module);

    const metricsDictName = allSettings.metric?.dictionary || "metrics";
    const metricsDict = this.inputsDictionary[metricsDictName];

    if (Array.isArray(metricsDict)) {
      this.metricsDictionary = metricsDict;
    } else {
      this.metricsDictionary = [];
      console.warn(`[ChartWithLegend] metrics dictionary '${metricsDictName}' not found`);
    }
  }

  protected showLegend(inputNames: string[], legendData: [], sortByNames = true, isClickable = true): void {
    if (!this.settings.legendPosition) return;

    let legendNames: LegendNames[] = inputNames.map((name, index) => ({ name, index }));

    if (sortByNames) {
      const other = legendNames.find((el) => el.name === Lang.other);
      legendNames = legendNames.filter((el) => el.name !== Lang.other);
      legendNames = orderBy(legendNames, ["name"], ["asc"]);
      if (!isEmpty(other)) legendNames.push(other);
    }

    const legendItems = legendNames.map((el) => legendData[el.index]) as LegendData;

    let legendSetting;
    const defaultSettingX = {
      centerX: p50,
      x: p50,
      marginTop: 20,
      layout: this.root.gridLayout,
      useDefaultMarker: true,
    };

    const defaultSettingY = {
      centerY: p50,
      y: percent(50),
      layout: this.root.verticalLayout,
      useDefaultMarker: true,
    };

    switch (this.settings.legendPosition) {
      case "left":
        legendSetting = defaultSettingY;
        break;
      case "right":
        legendSetting = { ...defaultSettingY, marginLeft: 40 };
        break;
      case "top":
      case "bottom":
        legendSetting = defaultSettingX;
        break;
      default:
        legendSetting = defaultSettingX;
        console.error(`Bad legend position '${this.settings.legendPosition}' for chart '${this.chartName}'.`);
    }

    const preparedLegendSettings = { ...legendSetting } as Record<string, unknown>;
    if (!isClickable) preparedLegendSettings.clickTarget = "none";

    const legend =
      this.settings.legendPosition === "top" || this.settings.legendPosition === "left"
        ? (this.chart as XYChart).children.unshift(Legend.new(this.root, preparedLegendSettings))
        : (this.chart as XYChart).children.push(Legend.new(this.root, preparedLegendSettings));

    legend.valueLabels.template.setAll({ forceHidden: true });
    legend.labels.template.setAll({
      maxWidth: MAX_WIDTH_LEGEND,
      oversizedBehavior: "truncate",
      ...chartFontSettings.legend,
    });

    legend.markerRectangles.template.setAll({
      cornerRadiusTL: CORNER_RADIUS_LEGEND,
      cornerRadiusTR: CORNER_RADIUS_LEGEND,
      cornerRadiusBL: CORNER_RADIUS_LEGEND,
      cornerRadiusBR: CORNER_RADIUS_LEGEND,
    });

    legend.markers.template.setAll({
      width: 12,
      height: 12,
      marginRight: 4,
    });

    legend.data.setAll(legendItems);
  }

  protected createLegendLabel(series: Series, metricValue: string): void {
    if (!this.metricsDictionary) this.getMetricsDictionary();

    const metricDictionary = this.metricsDictionary.find((metric) => metric.value === metricValue);
    series.set("legendLabelText", metricDictionary ? metricDictionary.title : metricValue);
  }
}

export default ChartWithLegend;
